import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import * as React from 'react';
import { useLocation } from 'react-router';

export interface DevsiteRouterContextModel {
  trigger404: () => void;
  trigger403: () => void;
}

export const ErrorPageContext = React.createContext<DevsiteRouterContextModel>({
  trigger403: () => null,
  trigger404: () => null,
});

interface ErrorPageProviderProps {
  render403: () => React.ReactNode;
  render404: () => React.ReactNode;
}

const ErrorPageProvider: React.FC<React.PropsWithChildren<ErrorPageProviderProps>> = ({
  render403,
  render404,
  children,
}) => {
  const { pathname } = useLocation();
  const [is403, setIs403] = useState(false);
  const [is404, setIs404] = useState(false);
  const pastLocation = useRef('');

  const trigger404 = useCallback(() => setIs404(true), [setIs404]);
  const trigger403 = useCallback(() => setIs403(true), []);

  useEffect(() => {
    if (pastLocation.current && pastLocation.current !== pathname) {
      setIs403(false);
      setIs404(false);
    }
    pastLocation.current = pathname;
  }, [pathname]);

  return (
    <ErrorPageContext.Provider value={{ trigger403, trigger404 }}>
      {is403 ? render403() : is404 ? render404() : children}
    </ErrorPageContext.Provider>
  );
};

export const Error404Trigger: React.FC = () => {
  const errorPageContext = useContext(ErrorPageContext);
  useEffect(() => {
    errorPageContext.trigger404();
  }, []);

  return null;
};

export const Error403Trigger: React.FC = () => {
  const errorPageContext = useContext(ErrorPageContext);
  useEffect(() => {
    errorPageContext.trigger403();
  }, []);

  return null;
};

export default ErrorPageProvider;
